import _ from 'lodash';
import * as DT from './model';
import {
  replacePage,
  removePageToLoad,
  deriveState,
  isInVersion,
  clearData,
  isAllTableSelected,
  resetSelection,
} from './utils';
import { EActionTypes as ActionTypes } from './actionTypes';
import { Action } from '../../models';
import { toogleArrayItem } from '../../utils';
import Logger from '../../libs/logger';   import { LogArea } from '../../libs/logger/const';

export const searchPageFetch = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  pageNumber: number,
  version: number
) => {
  if (!isInVersion(state.data, version)) return state;

  const updatedPage = { ...DT.DataPageDefault(), pageNumber, isFetching: true };
  const pages = replacePage(state.data.pages, updatedPage);
  const data = { ...state.data, pages };

  const pagesToLoad = removePageToLoad(state.pagesToLoad, pageNumber);

  const middleState = { ...state, data, pagesToLoad };
  return deriveState(middleState);
};

export const searchPageSuccess = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  pageNumber: number,
  items: T[],
  total: number,
  version: number
) => {
  if (!isInVersion(state.data, version)) return state;

  const updatedPage = {
    ...DT.DataPageDefault(),
    isFetching: false,
    pageNumber,
    items,
    isLoaded: true,
  };

  const totalChanged = state.data.total !== total;
  // when that happens it needs to discard all other pages... data is no longer trustable
  const pages = totalChanged
    ? [updatedPage]
    : replacePage(state.data.pages, updatedPage);
  const data = { ...state.data, pages, total };

  const middleState = { ...state, data };
  return deriveState(middleState);
};

export const searchPageError = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  pageNumber: number,
  version: number
) => {
  if (!isInVersion(state.data, version)) return state;

  const updatedPage = {
    ...DT.DataPageDefault(),
    pageNumber,
    isFetching: false,
  };
  const pages = replacePage(state.data.pages, updatedPage);
  const data = { ...state.data, pages };

  const pagesToLoad = removePageToLoad(state.pagesToLoad, pageNumber);

  const middleState = { ...state, data, pagesToLoad };
  const derivedState = deriveState(middleState);
  return { ...derivedState, pagesToLoad };
};

export const tableCurrentPageChange = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  pageNumber: number
) => {
  const pagination: DT.TablePagination = {
    ...state.table.pagination,
    currentPage: pageNumber,
  };
  const table = { ...state.table, pagination };
  return deriveState({ ...state, table });
};

export const tablePageSizeChange = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  pageSize: number
) => {
  const pagination: DT.TablePagination = {
    ...state.table.pagination,
    pageSize,
  };
  const table = { ...state.table, pagination };
  return deriveState({ ...state, table });
};

export const tableSortCriteriaChange = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  sort: DT.SortCriteria[]
) => {
  const data: DT.IData<T> = clearData(state.data);
  const selection = resetSelection(state.selection);
  const middleState = { ...state, sort, data, selection };
  return deriveState(middleState);
};

export const toggleSelectedAll = <T extends {}>(
  state: DT.IDatatableState<T, any>
) => {
  const isAllSelected = !state.selection.isAllSelected;
  const tablePKeys = state.table.content.map(state.selection.getPK);
  const selectedKeys = !isAllSelected
    ? _.difference(state.selection.selectedKeys, tablePKeys)
    : _.union(state.selection.selectedKeys, tablePKeys);
  const selection = { ...state.selection, selectedKeys, isAllSelected };
  return { ...state, selection };
};

export const toggleSelectedItem = <T extends {}>(
  state: DT.IDatatableState<T, any>,
  item: T
) => {
  const key = state.selection.getPK(item);
  const selectedKeys = toogleArrayItem(state.selection.selectedKeys, key);
  const selection = { ...state.selection, selectedKeys };
  selection.isAllSelected = isAllTableSelected(state.table, selection);
  return { ...state, selection };
};

export const clearSelection = <T extends {}>(
  state: DT.IDatatableState<T, any>
) => {
  const selection = resetSelection(state.selection);
  return { ...state, selection };
};

const Reducer = <T extends {}, F extends {}>(
  state: DT.IDatatableState<T, F>,
  action: Action<any>
) => {
  switch (action.type) {
    case ActionTypes.SEARCH_FETCH: {
      Logger.info([ActionTypes.SEARCH_FETCH, action], LogArea.Redux);
      const {
        pageNumber,
        version,
      }: DT.SearchPageSuccessPayload<T> = action.payload;
      return searchPageFetch(state, pageNumber, version);
    }
    case ActionTypes.SEARCH_SUCCESS: {
      const {
        pageNumber,
        items,
        total,
        version,
      }: DT.SearchPageSuccessPayload<T> = action.payload;
      return searchPageSuccess(state, pageNumber, items, total, version);
    }
    case ActionTypes.SEARCH_ERROR: {
      Logger.info([ActionTypes.SEARCH_ERROR, action], LogArea.Redux);
      const {
        pageNumber,
        version,
      }: DT.SearchPageSuccessPayload<T> = action.payload;
      return searchPageError(state, pageNumber, version);
    }

    case ActionTypes.TABLE_PAGE_CHANGE: {
      Logger.info(
        [ActionTypes.TABLE_PAGE_CHANGE, action],
        LogArea.Redux
      );
      const { pageNumber }: DT.TablePageChangedPayload = action.payload;
      return tableCurrentPageChange(state, pageNumber);
    }

    case ActionTypes.TABLE_PAGESIZE_CHANGE: {
      Logger.info(
        [ActionTypes.TABLE_PAGESIZE_CHANGE, action],
        LogArea.Redux
      );
      const { pageSize }: DT.TablePageSizeChangedPayload = action.payload;
      return tablePageSizeChange(state, pageSize);
    }

    case ActionTypes.SORT_CRITERIA_UPADATED: {
      Logger.info(
        [ActionTypes.SORT_CRITERIA_UPADATED, action],
        LogArea.Redux
      );
      const { sort }: DT.SortCriteriaPayload = action.payload;
      return tableSortCriteriaChange(state, sort);
    }

    case ActionTypes.TABLE_TOGGLE_ITEM: {
      Logger.info(
        [ActionTypes.TABLE_TOGGLE_ITEM, action],
        LogArea.Redux
      );
      const { item }: DT.TableToggleItemPayload = action.payload;
      return toggleSelectedItem(state, item);
    }

    case ActionTypes.TABLE_SELECT_ITEM: {
      Logger.info(
        [ActionTypes.TABLE_TOGGLE_ITEM, action],
        LogArea.Redux
      );
      const { item }: DT.TableToggleItemPayload = action.payload;
      const intermidateState = clearSelection(state);
      return toggleSelectedItem(intermidateState, item);
    }

    case ActionTypes.TABLE_TOGGLE_ALL: {
      Logger.info([ActionTypes.TABLE_TOGGLE_ALL, action], LogArea.Redux);
      return toggleSelectedAll(state);
    }

    case ActionTypes.CLEAR_SELECTION: {
      Logger.info([ActionTypes.CLEAR_SELECTION, action], LogArea.Redux);
      return clearSelection(state);
    }

    default: {
      return state;
    }
  }
};

export default Reducer;
